import type { ReactNode } from "react";
import { memo, useEffect, useState } from "react";
import type { IconType } from "react-icons";
import {
  FaCheckCircle,
  FaInfoCircle,
  FaExclamationTriangle,
  FaTimesCircle,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";

export type ToastVariant = "success" | "info" | "warning" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void;
  /** "primary" is the highlighted, recommended choice. */
  style?: "primary" | "secondary";
}

interface AppToastProps {
  title?: string;
  message: string | ReactNode;
  variant: ToastVariant;
  /** Buttons rendered under the message. Used by `toaster.confirm`. */
  actions?: ToastAction[];
  /**
   * When set, a "…in Ns" counter is shown next to the actions so the user can
   * see how long they have before the default action is taken for them.
   */
  countdownMs?: number;
  /** Label the countdown is describing, e.g. "Continuing". */
  countdownLabel?: string;
  onClose?: () => void;
}

const icons: Record<ToastVariant, { Icon: IconType; className: string }> = {
  success: { Icon: FaCheckCircle, className: "text-primary" },
  info: { Icon: FaInfoCircle, className: "text-blue-500" },
  warning: { Icon: FaExclamationTriangle, className: "text-orange-500" },
  error: { Icon: FaTimesCircle, className: "text-red-500" },
};

const styles: Record<ToastVariant, string> = {
  success: "border-primary",
  info: "border-blue-400",
  warning: "border-orange-400",
  error: "border-red-400",
};

function Countdown({ ms, label }: { ms: number; label?: string }) {
  const [remaining, setRemaining] = useState(() => Math.ceil(ms / 1000));

  useEffect(() => {
    const deadline = Date.now() + ms;
    const interval = setInterval(() => {
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(next);
      if (next <= 0) clearInterval(interval);
    }, 250);
    return () => clearInterval(interval);
  }, [ms]);

  return (
    <span className="text-xs font-medium text-appText/60">
      {label ? `${label} in ${remaining}s` : `${remaining}s`}
    </span>
  );
}

function AppToast({
  title,
  message,
  variant,
  actions,
  countdownMs,
  countdownLabel,
  onClose,
}: AppToastProps) {
  const { Icon, className } = icons[variant];

  return (
    <div
      className={`flex w-full items-start gap-3 rounded-2xl border-2 bg-cardBg px-4 py-3 text-start text-appText ${styles[variant]}`}
    >
      <div className="mt-1">
        <Icon className={className} size={22} />
      </div>

      <div className="flex-1">
        <div className="mb-1 text-sm font-semibold leading-[1.2em] text-appText">
          {title}
        </div>
        <div className="text-sm font-medium leading-[1.2em] text-appText/80 opacity-90">
          {message}
        </div>

        {actions && actions.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                className={
                  action.style === "primary"
                    ? "rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-all hover:opacity-90"
                    : "rounded-lg border border-appText/20 px-3 py-1.5 text-xs font-semibold text-appText/80 transition-all hover:bg-appText/10"
                }
              >
                {action.label}
              </button>
            ))}
            {countdownMs ? (
              <Countdown ms={countdownMs} label={countdownLabel} />
            ) : null}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onClose}
        className="shrink-0 self-center rounded-lg p-1 text-appText/50 transition-all hover:bg-cardBg/70 hover:text-appText/80"
      >
        <MdClose size={20} />
      </button>
    </div>
  );
}

export default memo(AppToast);
